<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCompanyNotesStore } from 'dashboard/stores/companyNotes';
import { useAlert } from 'dashboard/composables';

import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CompanyNoteItem from './components/CompanyNoteItem.vue';

const { t } = useI18n();
const route = useRoute();
const companyNotesStore = useCompanyNotesStore();

const state = reactive({
  message: '',
});

const companyId = computed(() => parseInt(route.params.companyId));

const uiFlags = computed(() => companyNotesStore.getUIFlags);
const isFetchingNotes = computed(() => uiFlags.value.fetchingList);
const isCreatingNote = computed(() => uiFlags.value.creating);
const notes = computed(() => companyNotesStore.getNotes);

const onAdd = async content => {
  if (!content) return;
  try {
    await companyNotesStore.create(companyId.value, content);
    state.message = '';
    useAlert(t('COMPANIES.NOTES.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(t('COMPANIES.NOTES.CREATE_ERROR'));
  }
};

const onDelete = async noteId => {
  if (!noteId) return;
  try {
    await companyNotesStore.delete(companyId.value, noteId);
    useAlert(t('COMPANIES.NOTES.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('COMPANIES.NOTES.DELETE_ERROR'));
  }
};

const keyboardEvents = {
  '$mod+Enter': {
    action: () => onAdd(state.message),
    allowOnFocusedInput: true,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div class="flex flex-col gap-6 py-6">
    <Editor
      v-model="state.message"
      :placeholder="t('COMPANIES.NOTES.PLACEHOLDER')"
      focus-on-mount
      class="[&>div]:!border-transparent [&>div]:px-4 [&>div]:py-4 px-6"
    >
      <template #actions>
        <div class="flex items-center gap-3">
          <Button
            variant="link"
            color="blue"
            size="sm"
            :label="t('COMPANIES.NOTES.ADD')"
            class="hover:no-underline"
            :is-loading="isCreatingNote"
            :disabled="!state.message || isCreatingNote"
            @click="onAdd(state.message)"
          />
        </div>
      </template>
    </Editor>
    <div
      v-if="isFetchingNotes"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div v-else-if="notes.length > 0">
      <CompanyNoteItem
        v-for="note in notes"
        :key="note.id"
        class="mx-6 py-4"
        :note="note"
        allow-delete
        @delete="onDelete"
      />
    </div>
    <p v-else class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11">
      {{ t('COMPANIES.NOTES.EMPTY') }}
    </p>
  </div>
</template>
